import { useEffect, useRef } from 'react'
import { env } from '../api'

const TURNSTILE_POLL_MS = 50
const TURNSTILE_POLL_MAX_ATTEMPTS = 200

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string
      theme?: string
      size?: string
      callback?: (token: string) => void
      'expired-callback'?: () => void
      'error-callback'?: () => void
    },
  ) => string
  remove?: (widgetId: string) => void
}

function getTurnstile(): TurnstileApi | undefined {
  return (window as Window & { turnstile?: TurnstileApi }).turnstile
}

type TurnstileFieldProps = {
  onToken: (token: string) => void
  onUnavailable?: () => void
}

export function TurnstileField({ onToken, onUnavailable }: TurnstileFieldProps) {
  const turnstileRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const onTokenRef = useRef(onToken)
  const onUnavailableRef = useRef(onUnavailable)
  onTokenRef.current = onToken
  onUnavailableRef.current = onUnavailable

  useEffect(() => {
    const el = turnstileRef.current
    if (!el) {
      return
    }

    const notifyToken = (token: string) => {
      queueMicrotask(() => onTokenRef.current(token))
    }

    const tryMount = (): boolean => {
      if (widgetIdRef.current) {
        return true
      }

      const turnstile = getTurnstile()
      if (!turnstile?.render) {
        return false
      }

      try {
        widgetIdRef.current = turnstile.render(el, {
          sitekey: env.turnstileSiteKey,
          theme: 'light',
          size: 'flexible',
          callback: notifyToken,
          'expired-callback': () => notifyToken(''),
          'error-callback': () => notifyToken(''),
        })
        return true
      } catch {
        onUnavailableRef.current?.()
        return false
      }
    }

    let intervalId: number | undefined
    let attempts = 0

    if (!tryMount()) {
      intervalId = window.setInterval(() => {
        attempts += 1
        if (tryMount() || attempts >= TURNSTILE_POLL_MAX_ATTEMPTS) {
          if (intervalId) {
            window.clearInterval(intervalId)
          }
          if (!widgetIdRef.current) {
            onUnavailableRef.current?.()
          }
        }
      }, TURNSTILE_POLL_MS)
    }

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId)
      }
      const widgetId = widgetIdRef.current
      if (widgetId) {
        getTurnstile()?.remove?.(widgetId)
        widgetIdRef.current = null
      }
    }
  }, [])

  return (
    <div className="turnstile-field">
      <div ref={turnstileRef} className="turnstile-widget" />
    </div>
  )
}
